import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { kanbanResponseSchema, type Dominio } from './types';

// Endpoints por dominio. APIs diferem em paths, query params e nome do campo
// ao mover (coluna_para vs coluna). buildKanbanUrl centraliza a lógica por pipe.
interface DominioConfig {
  buildKanbanUrl: (pipe?: string) => string;
  moverPath: (id: string) => string;
  moverField: 'coluna_para' | 'coluna' | 'kanban_coluna';
  detailPath: (id: string) => string;
  patchPath: (id: string) => string;
  responsaveisPath?: (id: string) => string;
  comentariosPath?: (id: string) => string;
  // Indica se POST /:id/comentarios/:cid/upload existe (anexos).
  comentariosUploadSupported?: boolean;
  historicoPath?: (id: string) => string;
}

const DOMINIO_CONFIG: Record<Dominio, DominioConfig> = {
  fundiario: {
    // GEO+CCIR no mesmo endpoint, filtra por tipo_processo. Titulação tem path próprio.
    buildKanbanUrl: (pipe) => {
      if (pipe === 'titulacao') return '/api/v2/kanban/titulacao';
      if (pipe === 'ccir') return '/api/v2/processos-geo/kanban?tipo_processo=ccir';
      if (pipe === 'triagem_geo') return '/api/v2/processos-geo/kanban?tipo_processo=triagem_geo';
      if (pipe === 'geo')
        return '/api/v2/processos-geo/kanban?tipo_processo=georreferenciamento';
      return '/api/v2/processos-geo/kanban';
    },
    moverPath: (id) => `/api/v2/processos-geo/${id}/mover`,
    moverField: 'coluna_para',
    detailPath: (id) => `/api/v2/processos-geo/${id}`,
    patchPath: (id) => `/api/v2/processos-geo/${id}`,
    responsaveisPath: (id) => `/api/v2/processos-geo/${id}/responsaveis`,
    comentariosPath: (id) => `/api/v2/processos-geo/${id}/comentarios`,
    comentariosUploadSupported: true,
  },
  ambiental: {
    buildKanbanUrl: (pipe) =>
      pipe
        ? `/api/v2/processos-ambiental/kanban?pipe=${encodeURIComponent(pipe)}`
        : '/api/v2/processos-ambiental/kanban',
    moverPath: (id) => `/api/v2/processos-ambiental/${id}/mover`,
    moverField: 'coluna',
    detailPath: (id) => `/api/v2/processos-ambiental/${id}`,
    patchPath: (id) => `/api/v2/processos-ambiental/${id}`,
    responsaveisPath: (id) => `/api/v2/processos-ambiental/${id}/responsaveis`,
    comentariosPath: (id) => `/api/v2/processos-ambiental/${id}/comentarios`,
    comentariosUploadSupported: false,
    historicoPath: (id) => `/api/v2/processos-ambiental/${id}/eventos`,
  },
  credito: {
    buildKanbanUrl: (pipe) =>
      pipe
        ? `/api/v2/processos-credito/kanban?pipe=${encodeURIComponent(pipe)}`
        : '/api/v2/processos-credito/kanban',
    moverPath: (id) => `/api/v2/processos-credito/${id}/mover`,
    moverField: 'kanban_coluna',
    detailPath: (id) => `/api/v2/processos-credito/${id}`,
    patchPath: (id) => `/api/v2/processos-credito/${id}`,
    responsaveisPath: (id) => `/api/v2/processos-credito/${id}/responsaveis`,
    comentariosPath: (id) => `/api/v2/processos-credito/${id}/comentarios`,
    comentariosUploadSupported: false,
    historicoPath: (id) => `/api/v2/processos-credito/${id}/eventos`,
  },
  juridico: {
    buildKanbanUrl: (pipe) =>
      pipe
        ? `/api/v2/juridico/kanban?pipe=${encodeURIComponent(pipe)}`
        : '/api/v2/juridico/kanban',
    moverPath: (id) => `/api/v2/juridico/processos/${id}/mover`,
    moverField: 'coluna',
    detailPath: (id) => `/api/v2/juridico/processos/${id}`,
    patchPath: (id) => `/api/v2/juridico/processos/${id}`,
    historicoPath: (id) => `/api/v2/juridico/processos/${id}/eventos`,
  },
};

export function useKanban(dominio: Dominio, pipe?: string) {
  const cfg = DOMINIO_CONFIG[dominio];
  const url = cfg.buildKanbanUrl(pipe);
  return useQuery({
    queryKey: ['kanban', dominio, pipe ?? 'default'] as const,
    queryFn: ({ signal }) => api(url, { schema: kanbanResponseSchema, signal }),
    staleTime: 30_000,
  });
}

export function useMoverCard(dominio: Dominio, pipe?: string) {
  const cfg = DOMINIO_CONFIG[dominio];
  const qc = useQueryClient();
  const queryKey = ['kanban', dominio, pipe ?? 'default'] as const;
  return useMutation({
    mutationFn: ({
      id,
      coluna,
      extra,
    }: {
      id: string;
      coluna: string | number;
      // Campos opcionais que o backend pode exigir ao mover (ex.: motivo
      // da perda quando entra em "Perdido" no FNO). Mesclados ao body.
      extra?: Record<string, unknown>;
    }) =>
      api(cfg.moverPath(id), {
        method: 'POST',
        body: { [cfg.moverField]: coluna, ...(extra ?? {}) },
        schema: z.object({ ok: z.boolean().optional() }).passthrough(),
      }),
    // Optimistic update: move o card no cache antes do back responder.
    // Em caso de erro, restaura o snapshot.
    onMutate: async ({ id, coluna }) => {
      await qc.cancelQueries({ queryKey });
      const snapshot = qc.getQueryData(queryKey);
      qc.setQueryData(queryKey, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const board = old as { colunas?: Array<{ id: string; processos: unknown[] }> };
        if (!board.colunas) return old;
        const targetId = String(coluna);
        let movingCard: unknown = null;
        const newColunas = board.colunas.map((c) => {
          const idx = c.processos.findIndex(
            (p: unknown) => (p as { id?: string }).id === id,
          );
          if (idx >= 0) {
            movingCard = c.processos[idx];
            return {
              ...c,
              processos: c.processos.filter(
                (p: unknown) => (p as { id?: string }).id !== id,
              ),
            };
          }
          return c;
        });
        if (!movingCard) return old;
        return {
          ...board,
          colunas: newColunas.map((c) =>
            c.id === targetId
              ? { ...c, processos: [...c.processos, movingCard] }
              : c,
          ),
        };
      });
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(queryKey, ctx.snapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });
}

export function useProcessoDetail(dominio: Dominio, id: string | null) {
  const cfg = DOMINIO_CONFIG[dominio];
  return useQuery({
    queryKey: ['kanban', dominio, 'detail', id ?? ''] as const,
    queryFn: ({ signal }) =>
      api(cfg.detailPath(id ?? ''), {
        schema: z.record(z.unknown()),
        signal,
      }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

// Substitui a lista de responsáveis (PUT). Disponível só nos dominios que
// têm responsaveisPath configurado.
export function useSetResponsaveis(dominio: Dominio, id: string | null, pipe?: string) {
  const cfg = DOMINIO_CONFIG[dominio];
  const qc = useQueryClient();
  const detailKey = ['kanban', dominio, 'detail', id ?? ''] as const;
  const boardKey = ['kanban', dominio, pipe ?? 'default'] as const;
  return useMutation({
    mutationFn: ({ userIds, motivo }: { userIds: number[]; motivo: string }) => {
      if (!cfg.responsaveisPath) throw new Error(`Domínio ${dominio} não suporta responsáveis`);
      return api(cfg.responsaveisPath(id ?? ''), {
        method: 'PUT',
        body: { user_ids: userIds, motivo },
        schema: z.unknown(),
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: detailKey });
      void qc.invalidateQueries({ queryKey: boardKey });
    },
  });
}

// Indica se o domínio tem suporte a edição de responsáveis (rota PUT).
export function hasResponsaveisSupport(dominio: Dominio): boolean {
  return !!DOMINIO_CONFIG[dominio].responsaveisPath;
}

export function hasComentariosSupport(dominio: Dominio): boolean {
  return !!DOMINIO_CONFIG[dominio].comentariosPath;
}

export function hasComentariosUploadSupport(dominio: Dominio): boolean {
  return !!DOMINIO_CONFIG[dominio].comentariosUploadSupported;
}

export function hasHistoricoSupport(dominio: Dominio): boolean {
  return !!DOMINIO_CONFIG[dominio].historicoPath;
}

// Histórico de eventos do processo. Cada domínio tem schema próprio mas
// todos têm pelo menos { id, tipo, descricao, criado_em, ator_nome? }.
const eventoSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String),
    tipo: z.string().nullish(),
    descricao: z.string().nullish(),
    criado_em: z.string().nullish(),
    ator_id: z.union([z.coerce.number(), z.string()]).nullish(),
    ator_nome: z.string().nullish(),
    coluna_de: z.union([z.coerce.number(), z.string()]).nullish(),
    coluna_para: z.union([z.coerce.number(), z.string()]).nullish(),
    coluna_de_nome: z.string().nullish(),
    coluna_para_nome: z.string().nullish(),
    substatus_de: z.string().nullish(),
    substatus_para: z.string().nullish(),
    processo_origem_id: z.union([z.coerce.number(), z.string()]).nullish(),
    processo_origem_tipo: z.string().nullish(),
    nivel_origem: z.coerce.number().nullish(),
    dados: z.unknown().nullish(),
  })
  .passthrough();
export type Evento = z.infer<typeof eventoSchema>;

export function useHistorico(dominio: Dominio, id: string | null) {
  const cfg = DOMINIO_CONFIG[dominio];
  return useQuery({
    queryKey: ['kanban', dominio, 'historico', id ?? ''] as const,
    queryFn: ({ signal }) => {
      if (!cfg.historicoPath) return Promise.resolve([] as Evento[]);
      return api(cfg.historicoPath(id ?? ''), {
        schema: z.array(eventoSchema),
        signal,
      });
    },
    enabled: !!id && !!cfg.historicoPath,
    staleTime: 30_000,
  });
}

// Comentários do processo — lista ordenada por created_at ASC.
export const comentarioAnexoSchema = z.object({
  nome: z.string(),
  url: z.string(),
  mime: z.string().nullish(),
  tamanho: z.coerce.number().nullish(),
});
export type ComentarioAnexo = z.infer<typeof comentarioAnexoSchema>;

const comentarioSchema = z.object({
  id: z.union([z.coerce.number(), z.string()]).transform(String),
  usuario_id: z.union([z.coerce.number(), z.string()]).nullish(),
  usuario_nome: z.string().nullish(),
  texto: z.string(),
  anexos: z.array(comentarioAnexoSchema).nullish(),
  created_at: z.string(),
});
export type Comentario = z.infer<typeof comentarioSchema>;

export function useComentarios(dominio: Dominio, id: string | null) {
  const cfg = DOMINIO_CONFIG[dominio];
  return useQuery({
    queryKey: ['kanban', dominio, 'comentarios', id ?? ''] as const,
    queryFn: ({ signal }) => {
      if (!cfg.comentariosPath) return Promise.resolve([] as Comentario[]);
      return api(cfg.comentariosPath(id ?? ''), {
        schema: z.array(comentarioSchema),
        signal,
      });
    },
    enabled: !!id && !!cfg.comentariosPath,
    staleTime: 15_000,
  });
}

// Cria o comentário e, se houver anexos, faz upload um por um na rota
// /comentarios/:cid/upload (FormData). Retorna o comentário com a lista
// final de anexos.
export function useAddComentario(dominio: Dominio, id: string | null) {
  const cfg = DOMINIO_CONFIG[dominio];
  const qc = useQueryClient();
  const queryKey = ['kanban', dominio, 'comentarios', id ?? ''] as const;
  return useMutation({
    mutationFn: async ({ texto, files }: { texto: string; files?: File[] }) => {
      if (!cfg.comentariosPath) throw new Error(`Domínio ${dominio} sem comentários`);
      const novo = await api(cfg.comentariosPath(id ?? ''), {
        method: 'POST',
        body: { texto },
        schema: comentarioSchema,
      });
      if (files && files.length > 0 && cfg.comentariosUploadSupported) {
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file, file.name);
          await api(`${cfg.comentariosPath(id ?? '')}/${novo.id}/upload`, {
            method: 'POST',
            body: fd,
            schema: z.unknown(),
          });
        }
      }
      return novo;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });
}

// PATCH com auto-save de um campo. Cache otimista + invalida o detalhe e o
// kanban no onSettled (caso o campo afete a visão do board, ex: status).
export function useUpdateProcesso(dominio: Dominio, id: string | null, pipe?: string) {
  const cfg = DOMINIO_CONFIG[dominio];
  const qc = useQueryClient();
  const detailKey = ['kanban', dominio, 'detail', id ?? ''] as const;
  const boardKey = ['kanban', dominio, pipe ?? 'default'] as const;
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api(cfg.patchPath(id ?? ''), {
        method: 'PATCH',
        body: patch,
        schema: z.record(z.unknown()),
      }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: detailKey });
      const snapshot = qc.getQueryData(detailKey);
      qc.setQueryData(detailKey, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        return { ...(old as Record<string, unknown>), ...patch };
      });
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(detailKey, ctx.snapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: detailKey });
      void qc.invalidateQueries({ queryKey: boardKey });
    },
  });
}
